import {
  computed,
  effect,
  inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type {} from '@geoman-io/leaflet-geoman-free';
import {
  divIcon,
  FeatureGroup,
  LatLng,
  Layer,
  Marker,
  marker,
  PM,
  Polygon,
  polygon,
  Polyline,
} from 'leaflet';
import { polygonHasSelfIntersection } from './geometry.util';
import { IMeasureVertex, MapService, MeasureType } from './map';

const BASIC_MEASURE_DRAW_OPTIONS: PM.DrawModeOptions = {
  templineStyle: {
    color: 'black',
    weight: 2,
    radius: 1,
  },
  hintlineStyle: {
    color: 'black',
    weight: 1,
    dashArray: [5, 5],
  },
  pathOptions: {
    color: 'black',
    weight: 2,
  },
  allowSelfIntersection: false,
};

@Injectable({
  providedIn: 'root',
})
export class MapMeasurementService implements OnDestroy {
  readonly mapService = inject(MapService);

  private _currentMeasureType = signal<MeasureType | null>(null);
  private _currentVertexes = signal<IMeasureVertex[]>([]);

  currentMeasureType = computed(() => this._currentMeasureType());
  isActive = computed(() => this._currentMeasureType() !== null);
  isDrawing = signal(false);

  readonly hasSelfIntersection = computed(() => {
    if (this._currentMeasureType() !== MeasureType.AREA || !this.isDrawing()) {
      return false;
    }

    // Strip consecutive duplicates (cursor resting on last placed vertex
    // creates a degenerate point that causes false positives)
    const points = this.currentVertexes()
      .map((v) => [v.latlng.lng, v.latlng.lat] as const)
      .filter(
        (p, i, arr) =>
          i === 0 || p[0] !== arr[i - 1][0] || p[1] !== arr[i - 1][1],
      );

    return polygonHasSelfIntersection(points);
  });

  private mousePos = toSignal(this.mapService.mousePos$);

  private firstVertexLatLng: LatLng | null = null;
  private snappedToFirstVertex = false;

  constructor() {
    let poly: Polygon | undefined = undefined;

    effect(() => {
      const measureType = this.currentMeasureType();

      if (measureType !== MeasureType.AREA || !this.isDrawing()) {
        poly?.remove();
        poly = undefined;
        return;
      }

      const vertices = this.currentVertexes();

      if (vertices.length < 3) {
        poly?.remove();
        poly = undefined;
        return;
      }

      const latlngs = vertices.map((v) => v.latlng);

      // Preview polygon
      // — excluded from GeoMan snapping to avoid self-snap interference
      poly =
        poly?.setLatLngs(latlngs) ||
        polygon(latlngs, {
          stroke: false,
          fillColor: 'black',
          snapIgnore: true,
        }).addTo(this.mapService.map);
    });
  }

  currentVertexes = computed(() => {
    const measureVertices = this._currentVertexes();
    const mousePos = this.mousePos();

    if (this.isDrawing() && mousePos) {
      return [
        ...measureVertices,
        { latlng: mousePos, id: `A${this.labelIndex}` },
      ];
    }

    return measureVertices;
  });

  // the working draw Layer from GeoMan
  private workingMeasureLayer: Layer | null = null;
  // The result layer generated by GeoMan after drawing, used to remove it later
  private drawnMeasureLayer: Layer | null = null;
  // the drawn features: Point labels, vertex markers and so on
  private drawnFeatureGroup = new FeatureGroup();
  private labelIndex = 1;

  private readonly labelMarkerClassName = 'measure-label-marker';

  private onDrawStart: PM.DrawStartEventHandler = (e) => {
    this.workingMeasureLayer = e.workingLayer;
    this.workingMeasureLayer?.on('pm:vertexadded', this.onVertexAdded);
    this.workingMeasureLayer?.on('pm:snap', this.onSnap);
    this.drawnFeatureGroup.addTo(this.mapService.map);
    this.firstVertexLatLng = null;
    this.snappedToFirstVertex = false;
  };

  /**
   * Snap - this event fires when cursor pulled to nearby vertex
   * We are using this to check whether user is clicking the first measure vertex.
   * This is a workaround since GeoMan does not provide the last clicked vertex info easily.
   *
   * @param e
   */
  private readonly onSnap: PM.SnapEventHandler = (e) => {
    const rawSnapLatLng = e.snapLatLng as LatLng | { lat: number; lng: number };
    const snapLatLng =
      rawSnapLatLng instanceof LatLng
        ? rawSnapLatLng
        : new LatLng(rawSnapLatLng.lat, rawSnapLatLng.lng);

    if (
      this.firstVertexLatLng &&
      this._currentMeasureType() === MeasureType.DISTANCE &&
      this._currentVertexes().length >= 2
    ) {
      const distance = snapLatLng.distanceTo(this.firstVertexLatLng);
      this.snappedToFirstVertex = distance < 1;
    } else {
      this.snappedToFirstVertex = false;
    }
  };

  private onDrawEnd: PM.DrawEndEventHandler = () => {
    this.finishDrawing();
  };

  private onDrawnShapeCreated: PM.CreateEventHandler = (e) => {
    this.drawnMeasureLayer = e.layer;
    /*
     * We close the polyline/polygan by adding a final line segment, if:
     * - Measure type is Distance and user snapped to first vertex when adding last vertex
     * - Measure type is Area (Polygon), we always close the polygon
     *  */
    const hasEnoughVertices = this._currentVertexes().length > 1;
    const isAreaMeasure = this._currentMeasureType() === MeasureType.AREA;
    const isDistanceClosedByClickFirstVertex =
      this._currentMeasureType() === MeasureType.DISTANCE &&
      this.snappedToFirstVertex;
    const shouldDrawClosingLine =
      hasEnoughVertices &&
      (isDistanceClosedByClickFirstVertex || isAreaMeasure);

    if (shouldDrawClosingLine) {
      const vertices = this._currentVertexes();
      const line = new Polyline(
        [vertices[vertices.length - 1].latlng, vertices[0].latlng],
        BASIC_MEASURE_DRAW_OPTIONS.pathOptions,
      );
      line.addTo(this.drawnFeatureGroup);
      this._currentVertexes.update((v) => [...v, v[0]]);
    }
  };

  private onVertexAdded: PM.VertexAddedEventHandler = (e) => {
    if (this._currentVertexes().length === 0) {
      this.firstVertexLatLng = e.latlng;
    }

    this.addMeasuredVertex(e.latlng);
  };

  ngOnDestroy(): void {
    this.removeDrawListeners();
  }

  public resetMeasure() {
    const measure = this.currentMeasureType();
    if (measure) {
      this.abortDrawing();
      this.startMeasure(measure);
    }
  }

  public startMeasure(type: MeasureType) {
    this.mapService.resetMapMode();
    this.clearDrawing();
    this.initDrawListeners();
    this._currentMeasureType.set(type);
    this.isDrawing.set(true);
    this.mapService.map.pm.enableDraw(
      type === MeasureType.DISTANCE ? 'Line' : 'Polygon',
      {
        ...BASIC_MEASURE_DRAW_OPTIONS,
        finishOn: 'dblclick',
      },
    );
  }

  public abortDrawing() {
    this._currentMeasureType.set(null);
    this.isDrawing.set(false);
    this.mapService.map.pm.disableDraw();
    this.removeDrawListeners();
  }

  public clearDrawing() {
    this.drawnFeatureGroup.clearLayers();
    this.workingMeasureLayer?.remove();
    this.drawnMeasureLayer?.remove();
    this.drawnMeasureLayer = null;
    this.workingMeasureLayer = null;
    this.isDrawing.set(false);
    this._currentMeasureType.set(null);
    this._currentVertexes.set([]);
    this.labelIndex = 1;
  }

  public finishDrawing() {
    this.mapService.map.pm.disableDraw();
    this.isDrawing.set(false);
    this.removeDrawListeners();
  }

  public undo() {
    if (!this._currentMeasureType() || this.currentVertexes().length <= 1)
      return;

    /* remove last vertex from GeoMan
     * Ref: https://github.com/geoman-io/leaflet-geoman/discussions/1469
     * */
    if (this._currentMeasureType() === MeasureType.DISTANCE) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.mapService.map.pm.Draw as any).Line._removeLastVertex();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.mapService.map.pm.Draw as any).Polygon._removeLastVertex();

      /* if measure area, and in finished state, from GeoMan if we remove last vertex,
       * the whole polygon will be removed, but our vertexMarkers and labels will remain.
       * So we remove everything to keep the state consistent.
       */
      if (this.drawnMeasureLayer) {
        this.clearDrawing();
        return;
      }
    }

    // Remove the last label (A B C) from the map
    const labelMarkers = this.drawnFeatureGroup
      .getLayers()
      .filter(
        (layer) =>
          layer instanceof Marker &&
          layer.getIcon().options.className === this.labelMarkerClassName,
      );
    const lastLabelMarker = labelMarkers[labelMarkers.length - 1];
    if (lastLabelMarker) {
      this.drawnFeatureGroup.removeLayer(lastLabelMarker);
    }

    // Remove the last drawn segment line from the polyline
    if (this.drawnMeasureLayer) {
      const layer = this.drawnMeasureLayer as Polyline;
      const latlngs = layer.getLatLngs();
      latlngs.pop();
      layer.setLatLngs(latlngs);
    }

    // last point label index should be decremented
    this.labelIndex--;

    // remove the last vertex from the current vertex array
    this._currentVertexes.update((vertices) => vertices.slice(0, -1));
  }

  private initDrawListeners() {
    this.mapService.map.on('pm:drawstart', this.onDrawStart);
    this.mapService.map.on('pm:drawend', this.onDrawEnd);
    this.mapService.map.on('pm:create', this.onDrawnShapeCreated);
  }

  private removeDrawListeners() {
    this.mapService.map.off('pm:drawstart', this.onDrawStart);
    this.mapService.map.off('pm:drawend', this.onDrawEnd);
    this.mapService.map.off('pm:create', this.onDrawnShapeCreated);
    if (this.workingMeasureLayer) {
      this.workingMeasureLayer.off('pm:vertexadded', this.onVertexAdded);
      this.workingMeasureLayer.off('pm:snap', this.onSnap);
    }
  }

  private addMeasuredVertex(latlng: LatLng) {
    const label = `A${this.labelIndex++}`;

    // Point label: A, B and so on
    marker(latlng, {
      icon: divIcon({
        className: this.labelMarkerClassName,
        html: `<span style="margin: 16px;">${label}</span>`,
      }),
      interactive: false,
    }).addTo(this.drawnFeatureGroup);

    // Add the new vertex to the current vertex array
    const newVertex: IMeasureVertex = {
      id: label,
      latlng,
    };
    this._currentVertexes.update((vertices) => [...vertices, newVertex]);
  }
}
